// AIOE owns it; the workbench copies it (D447).
/**
 * The organisation's approved Kubernetes clusters (D447): where agent work may run on a cluster, which node pools
 * serve which projects, and the requests people make to have a cluster approved. The list is the administrators':
 * AIOE writes the `clusters` of the organisation policy's `compute` clause from it (the clause itself is the
 * workbench's) so the workbench enforces what the console shows.
 *
 * AIOE never holds a kubeconfig, a token or a certificate: a cluster is named by its address or its Azure resource id,
 * and the credential it is reached with is the person's own sign-in, or an organisation secret named here by NAME.
 *
 *   GET    /compute/v1/clusters                               { v: 1, clusters } (ETag; a node sees only identity "organisation")
 *   POST   /compute/v1/clusters/requests                      a person asks the organisation to approve a cluster
 *   GET    /compute/v1/clusters/requests?mine=1               the caller's own requests
 *   GET/POST/PUT/DELETE /admin/v1/compute/clusters[/:id]      administrators
 *   GET    /admin/v1/compute/clusters/requests                administrators
 *   POST   /admin/v1/compute/clusters/requests/:id/decide     administrators
 *   POST   /projects/v1/:id/compute/join-keys                 a project's own single-use key for one of its cluster's pools (roles on)
 *   POST   /compute/v1/github/join                            public: a GitHub Actions run joins with its own OIDC token
 *
 * Plain zod, no Node.
 */
import { z } from "zod";
import { nodeQuotaSchema } from "./assignments.js";
export const COMPUTE_CLUSTERS_PATH = "/compute/v1/clusters";
export const COMPUTE_CLUSTER_REQUESTS_PATH = "/compute/v1/clusters/requests";
export const ADMIN_COMPUTE_CLUSTERS_PATH = "/admin/v1/compute/clusters";
export const ADMIN_COMPUTE_CLUSTER_REQUESTS_PATH = "/admin/v1/compute/clusters/requests";
export const COMPUTE_GITHUB_JOIN_PATH = "/compute/v1/github/join";
export const COMPUTE_CLUSTER_KINDS = ["aks", "azure-local", "kubernetes"];
export const COMPUTE_CLUSTER_IDENTITIES = ["person", "organisation"];
/** A project's own join key lives at most this long. */
export const PROJECT_JOIN_KEY_MAX_HOURS = 24;
/** A key a GitHub Actions run is given lives this long. */
export const GITHUB_JOIN_KEY_MINUTES = 30;
/**
 * Whether a text looks like a credential: a PEM block, a kubeconfig's certificate or token fields, a bearer token or a
 * JSON web token. AIOE refuses to keep such a thing anywhere in a cluster.
 */
export function looksLikeCredential(text) {
    return (/-----BEGIN [A-Z ]+-----/.test(text) ||
        /\b(client-certificate-data|client-key-data|certificate-authority-data|client-certificate|client-key)\b/i.test(text) ||
        /\bkind:\s*Config\b/.test(text) ||
        /\bbearer\s+[A-Za-z0-9._~+/=-]{16,}/i.test(text) ||
        /\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{4,}/.test(text) ||
        /\b(?:token|password|secret)\s*[:=]\s*\S{8,}/i.test(text));
}
const httpsServer = z
    .string()
    .max(500)
    .refine((v) => {
    try {
        const u = new URL(v);
        return u.protocol === "https:" && !u.username && !u.password && !u.search && !u.hash;
    }
    catch {
        return false;
    }
}, "the API server's https address, with no user name, password or query");
const label = z.string().trim().min(1).max(100);
const tag = z.string().trim().min(1).max(64);
export const approvedNodePoolSchema = z
    .object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/, "a lower-case name: letters, digits and dashes"),
    /** The namespace the pool lives in (the policy clause's pool names it); left out, the cluster's first namespace, else "default". */
    namespace: z.string().min(1).max(63).regex(/^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/, "a lower-case name: letters, digits and dashes").optional(),
    /** The tags a node joined to this pool carries (the placement rule reads them); AIOE always adds `pool:<name>`. */
    tags: z.array(tag).max(32),
    /** The runtime classes the pool offers (for example a gVisor or Kata class). */
    runtimeClasses: z.array(z.string().trim().min(1).max(63)).max(20).optional(),
    quota: nodeQuotaSchema.optional(),
})
    .strict();
const clusterFields = {
    kind: z.enum(COMPUTE_CLUSTER_KINDS),
    name: z.string().trim().min(1).max(80),
    aks: z.object({ resourceId: z.string().min(1).max(500), region: z.string().min(1).max(60) }).strict().optional(),
    azureLocal: z.object({ resourceId: z.string().min(1).max(500).optional(), cluster: label }).strict().optional(),
    /** The API server's address only; never a kubeconfig. */
    server: httpsServer.optional(),
    namespaces: z.array(z.string().trim().min(1).max(63)).max(50).optional(),
    nodePools: z.array(approvedNodePoolSchema).max(50).optional(),
    identity: z.enum(COMPUTE_CLUSTER_IDENTITIES),
    /** The NAME of an organisation secret (never its value); only with identity "organisation". */
    organisationSecret: z.string().min(1).max(100).optional(),
    foundryConnectionId: z.string().min(1).max(100).optional(),
    /** The projects this cluster's pools serve; empty or absent: any project. */
    projectIds: z.array(z.string().min(1).max(200)).max(200).optional(),
};
function strings(v, out = []) {
    if (typeof v === "string")
        out.push(v);
    else if (Array.isArray(v))
        for (const x of v)
            strings(x, out);
    else if (v && typeof v === "object")
        for (const x of Object.values(v))
            strings(x, out);
    return out;
}
/** Kind decides which part a cluster holds; no field may carry a credential. */
function checkCluster(v, ctx) {
    const bad = (path, message) => ctx.addIssue({ code: "custom", path: [path], message });
    if (v.kind === "aks") {
        if (!v.aks)
            bad("aks", "an AKS cluster names its Azure resource id and region");
        if (v.azureLocal)
            bad("azureLocal", "an AKS cluster has no Azure Local part");
    }
    else if (v.kind === "azure-local") {
        if (!v.azureLocal)
            bad("azureLocal", "an Azure Local cluster names the cluster");
        if (v.aks)
            bad("aks", "an Azure Local cluster has no AKS part");
    }
    else {
        if (!v.server)
            bad("server", "a Kubernetes cluster names its API server's address");
        if (v.aks || v.azureLocal)
            bad("aks", "a Kubernetes cluster has no Azure part");
    }
    if (v.organisationSecret && v.identity !== "organisation")
        bad("organisationSecret", "a secret goes with the organisation's own identity");
    if (strings(v).some(looksLikeCredential))
        bad("server", "a cluster holds an address, never a kubeconfig, a token or a certificate: the sign-in or an organisation secret is the credential");
    const names = (v.nodePools ?? []).map((p) => p.name);
    if (new Set(names).size !== names.length)
        bad("nodePools", "each node pool has its own name");
}
export const approvedClusterInputSchema = z.object(clusterFields).strict().superRefine(checkCluster);
export const approvedClusterSchema = z
    .object({ id: z.string().min(1).max(100), ...clusterFields, updatedAt: z.string(), updatedBy: z.string() })
    .strict()
    .superRefine(checkCluster);
export const approvedClustersAnswerSchema = z.object({ v: z.literal(1), clusters: z.array(approvedClusterSchema) });
// ── Asking the organisation to approve a cluster ───────────────────────────────────────────
export const COMPUTE_REQUEST_STATES = ["pending", "approved", "declined"];
export const computeClusterRequestInputSchema = z
    .object({
    ...clusterFields,
    reason: z.string().trim().max(1000).optional(),
})
    .strict()
    .superRefine(checkCluster);
/** One request as its person reads it. */
export const computeClusterRequestViewSchema = z.object({
    requestId: z.string(),
    kind: z.enum(COMPUTE_CLUSTER_KINDS),
    name: z.string(),
    state: z.enum(COMPUTE_REQUEST_STATES),
    note: z.string().optional(),
    createdAt: z.string(),
    decidedAt: z.string().optional(),
});
/** One request as administrators read it: the view, who asked and what for. */
export const computeClusterRequestSchema = computeClusterRequestViewSchema.extend({
    askedBy: z.string(),
    askedByName: z.string().optional(),
    /** What was asked for: a cluster without its id. */
    cluster: z.object(clusterFields).strict(),
    reason: z.string().optional(),
    decidedBy: z.string().optional(),
    clusterId: z.string().optional(),
});
export const computeRequestDecisionSchema = z.object({
    decision: z.enum(["approve", "decline"]),
    note: z.string().trim().max(1000).optional(),
});
// ── Join keys ──────────────────────────────────────────────────────────────────────────────
/** `POST /projects/v1/:id/compute/join-keys`. */
export const projectJoinKeyCreateSchema = z
    .object({ clusterId: z.string().min(1).max(100), nodePool: z.string().min(1).max(63), name: z.string().trim().min(1).max(200).optional() })
    .strict();
/** `POST /compute/v1/github/join`. */
export const githubJoinSchema = z.object({ idToken: z.string().min(20).max(8000), workflowRunId: z.string().max(40).optional() }).strict();
//# sourceMappingURL=computeClusters.js.map